import { useEffect, useRef, useCallback, useState } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useAnnotationStore } from '../../stores/annotationStore';
import { pdfEngine } from '../../core/PDFEngine';
import { PDFPage } from './PDFPage';
import './PDFViewer.css';

export function PDFViewer() {
  const { currentPage, totalPages, zoom, rotation, isLoading, loadingProgress, scrollMode, spreadMode, setCurrentPage, documents, activeDocumentId } = useDocumentStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visiblePages, setVisiblePages] = useState<number[]>([]);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const lastModified = activeDoc?.lastModified || 0;

  // Calculate visible pages based on scroll position
  useEffect(() => {
    if (scrollMode === 'single') {
      setVisiblePages([currentPage]);
      return;
    }

    // For continuous scroll, show a window of pages
    const buffer = 2;
    const start = Math.max(1, currentPage - buffer);
    const end = Math.min(totalPages, currentPage + buffer);
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    setVisiblePages(pages);
  }, [currentPage, totalPages, scrollMode]);

  // Handle scroll to update current page
  const handleScroll = useCallback(() => {
    if (!containerRef.current || scrollMode === 'single') return;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const scrollCenter = scrollTop + containerHeight / 2;

    // Find the page element closest to center
    const pageElements = container.querySelectorAll('.pdf-page-wrapper');
    let closestPage = currentPage;
    let closestDistance = Infinity;

    pageElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const pageCenter = rect.top - containerRect.top + rect.height / 2 + scrollTop;
      const distance = Math.abs(pageCenter - scrollCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = parseInt(el.getAttribute('data-page') || '1', 10);
      }
    });

    if (closestPage !== currentPage) {
      setCurrentPage(closestPage);
    }
  }, [currentPage, scrollMode, setCurrentPage]);

  // Scroll to current page when it changes
  useEffect(() => {
    if (!containerRef.current || scrollMode !== 'vertical') return;

    const pageEl = containerRef.current.querySelector(`[data-page="${currentPage}"]`);
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage, scrollMode]);

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          useDocumentStore.getState().zoomIn();
        } else {
          useDocumentStore.getState().zoomOut();
        }
      }
    },
    []
  );

  return (
    <div
      className="pdf-viewer"
      ref={containerRef}
      onScroll={handleScroll}
      onWheel={handleWheel}
    >
      {isLoading && (
        <div className="pdf-loading">
          <div className="pdf-loading-content animate-fade-in">
            <div className="animate-spin" style={{ fontSize: '2rem' }}>⚙️</div>
            <p>Loading document...</p>
            <div className="progress-bar" style={{ width: 200 }}>
              <div className="progress-bar-fill" style={{ width: `${loadingProgress * 100}%` }} />
            </div>
            <span className="pdf-loading-percent">{Math.round(loadingProgress * 100)}%</span>
          </div>
        </div>
      )}

      <div className="pdf-pages-container" style={{ flexDirection: 'column' }}>
        {(() => {
          const rows: number[][] = [];
          if (spreadMode === 'none') {
            visiblePages.forEach(p => rows.push([p]));
          } else {
            let i = 0;
            // For 'odd', page 1 is alone on the first row
            if (spreadMode === 'odd' && visiblePages[0] === 1) {
              rows.push([1]);
              i = 1;
            }
            while (i < visiblePages.length) {
              if (i + 1 < visiblePages.length) {
                // To properly align left/right pages, check if the first page is even/odd
                // But for simplicity with visiblePages (which might be a subset), just group by 2s
                // However, we should group based on the absolute page number to keep them consistent
                const p1 = visiblePages[i];
                const p2 = visiblePages[i + 1];
                const isP1Left = spreadMode === 'odd' ? p1 % 2 === 0 : p1 % 2 !== 0;
                
                if (isP1Left && p2 === p1 + 1) {
                  rows.push([p1, p2]);
                  i += 2;
                } else {
                  rows.push([p1]);
                  i += 1;
                }
              } else {
                rows.push([visiblePages[i]]);
                i++;
              }
            }
          }

          return rows.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="pdf-page-row" style={{ display: 'flex', gap: 'var(--space-4)', justifyContent: 'center' }}>
              {row.map((pageNum) => (
                <PDFPage
                  key={`${activeDocumentId}-${lastModified}-${pageNum}`}
                  pageNumber={pageNum}
                  scale={zoom}
                  rotation={rotation}
                />
              ))}
            </div>
          ));
        })()}
      </div>
    </div>
  );
}
